import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FaUsers,
  FaProjectDiagram,
  FaUserShield,
  FaCalendar,
  FaCheckCircle,
  FaChartBar,
} from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface DashboardStats {
  totalUsers: number;
  activeProjects: number;
  deletedProjects: number;
  adminUsers: number;
  paidKits: number;
}

interface UserAnalytics {
  total: number;
  active: number;
  disabled: number;
  deleted: number;
  verified: number;
  unverified: number;
  byRole: {
    agency_admin: number;
    agency_member: number;
    member: number;
    null: number;
  };
}

interface ProjectAnalytics {
  active: number;
  deleted: number;
  total: number;
  byStatus: Record<string, number>;
  deletedByStatus: Record<string, number>;
}

interface DocumentAnalytics {
  totalVersions: number;
  projectsWithDocuments: number;
}

interface OrganizationAnalytics {
  totalInvites: number;
  pendingInvites: number;
  acceptedInvites: number;
  expiredInvites: number;
}

interface GrowthData {
  _id: {
    year: number;
    month: number;
  };
  count: number;
}

interface TopIndustry {
  _id: string;
  count: number;
}

interface RecentUser {
  _id: string;
  name: string;
  email: string;
  createdAt: string;
  role?: string;
}

interface RecentProject {
  _id: string;
  projectName: string;
  userId: {
    name: string;
    email: string;
  };
  createdAt: string;
  projectStatus?: string;
  status?: string;
}

interface DashboardData {
  stats: DashboardStats;
  analytics: {
    users: UserAnalytics;
    projects: ProjectAnalytics;
    documents: DocumentAnalytics;
    organizations: OrganizationAnalytics;
  };
  recentUsers: RecentUser[];
  recentProjects: RecentProject[];
  growth: {
    users: GrowthData[];
    projects: GrowthData[];
  };
  topIndustries: TopIndustry[];
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PROJECT_STATUS_LABELS: Record<string, string> = {
  created: 'Created',
  core_questions_complete: 'Core Questions',
  core_business_generated: 'Core Business',
  persona_before_login_created: 'Persona (Pre-login)',
  follow_up_generated: 'Follow-up Generated',
  platform_info_created: 'Platform Info',
  persona_created: 'Personas Created',
  value_props_created: 'Value Props',
  features_generated: 'Features Generated',
  features_prioritized: 'Features Prioritized',
  requirements_generated: 'Requirements',
  document_generated: 'Document Generated',
};

const PROJECT_STATUS_COLORS: Record<string, string> = {
  created: 'bg-gray-300',
  core_questions_complete: 'bg-slate-400',
  core_business_generated: 'bg-blue-300',
  persona_before_login_created: 'bg-cyan-400',
  follow_up_generated: 'bg-blue-400',
  platform_info_created: 'bg-indigo-400',
  persona_created: 'bg-purple-400',
  value_props_created: 'bg-pink-400',
  features_generated: 'bg-orange-400',
  features_prioritized: 'bg-yellow-400',
  requirements_generated: 'bg-lime-500',
  document_generated: 'bg-green-500',
};

const PROJECT_STATUS_DOT: Record<string, string> = {
  created: 'bg-gray-400',
  core_questions_complete: 'bg-slate-500',
  core_business_generated: 'bg-blue-400',
  persona_before_login_created: 'bg-cyan-500',
  follow_up_generated: 'bg-blue-500',
  platform_info_created: 'bg-indigo-500',
  persona_created: 'bg-purple-500',
  value_props_created: 'bg-pink-500',
  features_generated: 'bg-orange-500',
  features_prioritized: 'bg-yellow-500',
  requirements_generated: 'bg-lime-600',
  document_generated: 'bg-green-600',
};

interface GrowthChartProps {
  data: GrowthData[];
  label: string;
  color: string;
}

const GrowthChart: React.FC<GrowthChartProps> = ({ data, label, color }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div>
      <div className="flex items-end gap-2 h-24">
        {data.map((d) => {
          const pct = Math.round((d.count / max) * 100);
          return (
            <div key={`${d._id.year}-${d._id.month}`} className="flex flex-col items-center flex-1 gap-1">
              <span className="text-xs font-semibold text-gray-700">{d.count}</span>
              <div className="w-full rounded-t-md transition-all" style={{ height: `${Math.max(pct, 8)}%`, backgroundColor: color }} />
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-1">
        {data.map((d) => (
          <div key={`label-${d._id.year}-${d._id.month}`} className="flex-1 text-center">
            <span className="text-xs text-gray-400">{MONTH_NAMES[d._id.month - 1]}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-2 text-center">{label}</p>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await api.get('/admin/dashboard');
      return (response.data.data || response.data) as DashboardData;
    },
  });


  if (isDashboardLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const stats = dashboardData?.stats || { totalUsers: 0, activeProjects: 0, deletedProjects: 0, adminUsers: 0, paidKits: 0 };
  const analytics = dashboardData?.analytics || {
    users: { total: 0, active: 0, disabled: 0, deleted: 0, verified: 0, unverified: 0, byRole: { agency_admin: 0, agency_member: 0, member: 0, null: 0 } },
    projects: { active: 0, deleted: 0, total: 0, byStatus: {}, deletedByStatus: {} },
    documents: { totalVersions: 0, projectsWithDocuments: 0 },
    organizations: { totalInvites: 0, pendingInvites: 0, acceptedInvites: 0, expiredInvites: 0 }
  };

  const recentUsers = dashboardData?.recentUsers || [];
  const recentProjects = dashboardData?.recentProjects || [];
  const topIndustries = dashboardData?.topIndustries || [];
  const growthUsers = dashboardData?.growth?.users || [];
  const growthProjects = dashboardData?.growth?.projects || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const statCards = [
    {
      label: 'Total Users',
      value: stats.totalUsers,
      sub: `Active: ${analytics.users.active}`,
      subColor: 'text-green-600',
      icon: <FaUsers className="h-5 w-5 text-blue-500" />,
      accent: 'border-blue-500',
      bg: 'bg-blue-50',
    },
    {
      label: 'Active Projects',
      value: stats.activeProjects,
      sub: `Deleted: ${stats.deletedProjects}`,
      subColor: 'text-red-500',
      icon: <FaProjectDiagram className="h-5 w-5 text-green-500" />,
      accent: 'border-green-500',
      bg: 'bg-green-50',
    },
    {
      label: 'Admin Users',
      value: stats.adminUsers,
      sub: 'System administrators',
      subColor: 'text-purple-500',
      icon: <FaUserShield className="h-5 w-5 text-purple-500" />,
      accent: 'border-purple-500',
      bg: 'bg-purple-50',
    },
    {
      label: 'Paid Kits',
      value: stats.paidKits,
      sub: 'Founder kit purchased',
      subColor: 'text-amber-600',
      icon: <FaCheckCircle className="h-5 w-5 text-amber-500" />,
      accent: 'border-amber-500',
      bg: 'bg-amber-50',
    },
  ];

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden">

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary to-primaryDark rounded-2xl p-6 lg:p-8 text-white"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Welcome back, Admin</h1>
            <p className="text-blue-200 text-sm sm:text-base">Here's your platform overview for today.</p>
          </div>
          <div className="hidden lg:flex items-center gap-6 text-right flex-shrink-0">
            <div>
              <p className="text-blue-200 text-xs uppercase tracking-wide mb-0.5">Total Users</p>
              <p className="text-3xl font-bold">{stats.totalUsers}</p>
            </div>
            <div className="w-px h-12 bg-white opacity-20" />
            <div>
              <p className="text-blue-200 text-xs uppercase tracking-wide mb-0.5">Active Projects</p>
              <p className="text-3xl font-bold">{stats.activeProjects}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * (i + 1) }}
            className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-5 border-l-4 ${card.accent} hover:shadow-md transition-shadow`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{card.label}</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">{card.value}</p>
                <p className={`text-xs font-medium ${card.subColor}`}>{card.sub}</p>
              </div>
              <div className={`p-2.5 rounded-xl ${card.bg} flex-shrink-0`}>
                {card.icon}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* User Analytics — full width */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-gray-900">User Analytics</h3>
          <FaUsers className="h-4 w-4 text-primary opacity-60" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { label: 'Active', value: analytics.users.active, dot: 'bg-green-500', bar: 'bg-green-500' },
            { label: 'Disabled', value: analytics.users.disabled, dot: 'bg-red-500', bar: 'bg-red-400' },
            { label: 'Deleted', value: analytics.users.deleted ?? 0, dot: 'bg-orange-400', bar: 'bg-orange-400' },
          ].map(row => {
            const pct = analytics.users.total > 0 ? Math.round((row.value / analytics.users.total) * 100) : 0;
            return (
              <div key={row.label}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${row.dot}`} />
                    <span className="text-sm text-gray-600">{row.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">{pct}%</span>
                    <span className="text-sm font-semibold text-gray-900 w-8 text-right">{row.value}</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${row.bar}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="bg-gray-50 rounded-xl p-3 text-center w-40">
            <p className="text-xs text-gray-500 mb-0.5">Total Users</p>
            <p className="text-xl font-bold text-gray-900">{analytics.users.total}</p>
          </div>
        </div>
      </motion.div>

      {/* Project Analytics — full width, active + deleted side by side */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-gray-900">Project Analytics</h3>
          <FaProjectDiagram className="h-4 w-4 text-primary opacity-60" />
        </div>

        <div className="flex gap-3 mb-6">
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-xs text-gray-500 mb-0.5">Active</p>
            <p className="text-2xl font-bold text-gray-900">{analytics.projects.active}</p>
          </div>
          <div className="flex-1 bg-red-50 rounded-xl p-3 text-center">
            <p className="text-xs text-red-500 mb-0.5">Deleted</p>
            <p className="text-2xl font-bold text-red-600">{analytics.projects.deleted}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active by status */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Active Projects by Status</h4>
            <div className="space-y-2.5">
              {(Object.entries(analytics.projects.byStatus) as [string, number][]).filter(([, count]) => count > 0).map(([status, count]) => {
                const activeTotal = analytics.projects.active || 1;
                const pct = Math.round((count / activeTotal) * 100);
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PROJECT_STATUS_DOT[status] || 'bg-gray-400'}`} />
                        <span className="text-xs text-gray-600">{PROJECT_STATUS_LABELS[status] || status}</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">{count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${PROJECT_STATUS_COLORS[status] || 'bg-gray-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Deleted by status */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Deleted Projects by Status</h4>
            <div className="space-y-2.5">
              {(Object.entries(analytics.projects.deletedByStatus) as [string, number][]).filter(([, count]) => count > 0).map(([status, count]) => {
                const deletedTotal = analytics.projects.deleted || 1;
                const pct = Math.round((count / deletedTotal) * 100);
                return (
                  <div key={`deleted-${status}`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PROJECT_STATUS_DOT[status] || 'bg-gray-400'}`} />
                        <span className="text-xs text-gray-600">{PROJECT_STATUS_LABELS[status] || status}</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">{count}</span>
                    </div>
                    <div className="w-full bg-red-50 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-red-300" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Growth Charts */}
      {(growthUsers.length > 0 || growthProjects.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
          {growthUsers.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">User Growth</h3>
                  <p className="text-xs text-gray-400 mt-0.5">New signups by month</p>
                </div>
                <FaChartBar className="h-4 w-4 text-primary opacity-60" />
              </div>
              <GrowthChart data={growthUsers} label="Monthly new users" color="#3B82F6" />
            </div>
          )}
          {growthProjects.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Project Growth</h3>
                  <p className="text-xs text-gray-400 mt-0.5">New projects by month</p>
                </div>
                <FaChartBar className="h-4 w-4 text-primary opacity-60" />
              </div>
              <GrowthChart data={growthProjects} label="Monthly new projects" color="#10B981" />
            </div>
          )}
        </motion.div>
      )}

      {/* Top Industries */}
      {topIndustries.filter(i => i._id !== null).length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-gray-900">Top Industries</h3>
            <FaProjectDiagram className="h-4 w-4 text-primary opacity-60" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {topIndustries.filter(i => i._id !== null).slice(0, 6).map((industry) => (
              <div key={industry._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-sm font-medium text-gray-700 truncate">{industry._id}</span>
                <span className="text-sm font-bold text-primary ml-2 flex-shrink-0">{industry.count}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Recent Users */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-gray-900">Recent Users</h3>
            <button
              onClick={() => navigate('/users')}
              className="text-xs text-primary font-medium hover:underline"
            >
              View all
            </button>
          </div>
          <div className="space-y-1">
            {recentUsers.map((user) => (
              <div
                key={user._id}
                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-primary to-primaryDark rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                  {getInitials(user.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-400 truncate">{user.email}</p>
                </div>
                <div className="flex items-center text-xs text-gray-300 flex-shrink-0 gap-1">
                  <FaCalendar className="h-3 w-3" />
                  <span>{formatDate(user.createdAt)}</span>
                </div>
              </div>
            ))}
            {recentUsers.length === 0 && (
              <div className="text-center py-10 text-gray-400">
                <FaUsers className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No recent users</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Projects */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-gray-900">Recent Projects</h3>
            <button
              onClick={() => navigate('/projects')}
              className="text-xs text-primary font-medium hover:underline"
            >
              View all
            </button>
          </div>
          <div className="space-y-1">
            {recentProjects.map((project) => (
              <div
                key={project._id}
                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                  <FaProjectDiagram className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{project.projectName}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-400 truncate">by {project.userId.name}</p>
                    {project.status && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        project.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {project.status}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center text-xs text-gray-300 flex-shrink-0 gap-1">
                  <FaCalendar className="h-3 w-3" />
                  <span>{formatDate(project.createdAt)}</span>
                </div>
              </div>
            ))}
            {recentProjects.length === 0 && (
              <div className="text-center py-10 text-gray-400">
                <FaProjectDiagram className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No recent projects</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-base font-semibold text-gray-900 mb-4">Quick Navigation</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/users')}
            className="flex items-center justify-center gap-3 p-4 border-2 border-primary rounded-xl text-primary hover:bg-primary hover:text-white transition-all font-medium text-sm"
          >
            <FaUsers className="h-4 w-4 flex-shrink-0" />
            Manage Users
          </button>
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center justify-center gap-3 p-4 border-2 border-green-500 rounded-xl text-green-600 hover:bg-green-500 hover:text-white transition-all font-medium text-sm"
          >
            <FaProjectDiagram className="h-4 w-4 flex-shrink-0" />
            View Projects
          </button>
        </div>
      </motion.div>

    </div>
  );
};

export default Dashboard;
